import { createAsyncThunk } from '@reduxjs/toolkit';
import { getProjectThreads } from 'store/chat/services/getProjectThreads.service';
import v4 from 'uuid/v4';

export const getProjectThreadsThunk = createAsyncThunk(
  'chat/getProjectThreads',
  async ({ projects, chatWebsocketRef, userId }, { getState }) => {
    const projectIds = projects.map((project) => project.id);
    const threadsResult = await getProjectThreads({ projectIds });
    const state = getState().chatSlice;
    const threadsByProject = state.threadsByProject || {};
    const activeThread = state.thread;

    let threads = {};
    for (const thread of threadsResult) {
      const project = projects.find((p) => p.id === thread.projectId);
      if (project) {
        threads[thread.projectId] = {
          ...thread,
          parent: project.parent,
          children: [],
          unreadMessagesInChildProjects: 0,
        };
        // Only through a socket that is open. At startup it may not exist
        // yet, while its URL waits for a ticket, and send() on a missing or
        // connecting socket throws, which failed this whole thunk and left the
        // app with no project threads. These are subscribed when the socket
        // opens instead, from the list App stores once this resolves.
        const socket = chatWebsocketRef.current;
        if (
          !threadsByProject[thread.projectId] &&
          socket?.readyState === WebSocket.OPEN
        ) {
          const socketValidObject = {
            newThreadConnection: {
              id: v4(),
              userId: userId,
              threadId: thread.id,
              isActive: thread.id === activeThread?.id,
            },
          };
          socket.send(JSON.stringify(socketValidObject));
        }
      }
    }

    for (const [projectId, thread] of Object.entries(threads)) {
      if (thread.parent) {
        let hasParents = true;
        let parentThread = threads[thread.parent];
        while (hasParents && parentThread) {
          parentThread.children.push(thread);
          parentThread.unreadMessagesInChildProjects +=
            thread.unreadMessagesCount;
          hasParents = Boolean(parentThread.parent);
          if (hasParents) {
            parentThread = threads[parentThread.parent];
          }
        }
      }
    }
    const threadsResultMap = threadsResult.reduce((acc, thread) => {
      acc[thread.id] = thread;
      return acc;
    }, {});

    return { threadsByProject: threads, threads: threadsResultMap };
  }
);
